import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import { Response } from "../interfaces";
import { EmailTemplate, TemplateEmailType } from "../../pages/settings/interfaces";
import { EmailTemplateData } from "../../pages/settings/components/AddEditTemplate";

export type KeyedTemplateRow = {
  template_key: string;
  label: string;
  template: EmailTemplate;
  is_customized: boolean;
};

export type KeyedTemplatesResponse = {
  email_type: TemplateEmailType;
  sender: "mailbox" | "system";
  templates: KeyedTemplateRow[];
};

export const emailTemplateApi = createApi({
  reducerPath: "emailTemplateApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["emailTemplate", "keyedEmailTemplate"],
  endpoints: (builder) => ({
    getEmailTemplateList: builder.query<Response<EmailTemplate[]>, any>({
      query: (credentials) => ({
        url: "emailTemplate/list?" + new URLSearchParams(credentials),
        method: "GET",
        data: credentials,
      }),
      providesTags: ["emailTemplate"],
    }),
    getKeyedEmailTemplates: builder.query<
      Response<KeyedTemplatesResponse>,
      { emailType: "reminder" | "transactional" }
    >({
      query: ({ emailType }) => ({
        url: `emailTemplate/keyed/${emailType}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { emailType }) => [
        { type: "keyedEmailTemplate", id: emailType },
      ],
    }),
    updateKeyedEmailTemplate: builder.mutation<
      Response<EmailTemplate>,
      {
        emailType: "reminder" | "transactional";
        templateKey: string;
        subject?: string;
        body?: string;
        text_message_body?: string;
      }
    >({
      query: ({ emailType, templateKey, ...rest }) => ({
        url: `emailTemplate/keyed/${emailType}/${templateKey}`,
        method: "PATCH",
        data: rest,
      }),
      invalidatesTags: (_result, _error, { emailType }) => [
        { type: "keyedEmailTemplate", id: emailType },
        "emailTemplate",
      ],
    }),
    createEmailTemplate: builder.mutation<
      Response<EmailTemplate[]>,
      EmailTemplateData
    >({
      query: (credentials) => ({
        url: "emailTemplate/create",
        method: "POST",
        data: credentials,
      }),
      invalidatesTags: ["emailTemplate"],
    }),
    updateEmailTemplate: builder.mutation<
      Response<EmailTemplate[]>,
      EmailTemplateData
    >({
      query: ({ id, ...rest }) => ({
        url: "emailTemplate/" + id,
        method: "PATCH",
        data: rest,
      }),
      invalidatesTags: ["emailTemplate"],
    }),
    deleteEmailTemplate: builder.mutation<Response<EmailTemplate[]>, string>({
      query: (emailTemplateId) => ({
        url: "emailTemplate/" + emailTemplateId,
        method: "DELETE",
      }),
      invalidatesTags: ["emailTemplate"],
    }),
  }),
});

export const {
  useGetEmailTemplateListQuery,
  useGetKeyedEmailTemplatesQuery,
  useLazyGetEmailTemplateListQuery,
  useUpdateEmailTemplateMutation,
  useUpdateKeyedEmailTemplateMutation,
  useCreateEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
} = emailTemplateApi;
